import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { connect } from "react-redux";
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { Box, CircularProgress } from "@mui/material";

import "./style.css";

import { PostEventDetails } from "../PostEventDetails";

import { RadioButtons4 } from "../../../icons/RadioButtons4";
import { RadioButtons5 } from "../../../icons/RadioButtons5";

import homeImage from '../../../icons/ImageIcons/material-symbols_home-rounded.svg';
import rightarrow from '../../../icons/ImageIcons/keyboard-arrow-right.svg';

import { Group } from "../../../components/Group";
import { UploadMediaClipSection } from "../../../components/UploadMediaClipSection";
import { TimestampFieldComponent } from "../../../components/TimestampFieldComponent";

import { getClientData, setClientData, toastError, toastSuccess } from "../../../constants/Constants";
import { dispatchPostRequest, fetchPostEventData, getCeremonyVideoUri, getHandshakeDetails, initializeValues, postFirstHandshakeTimestampDetailsWithInput, setEventSuccess, setPostEventDetails, submitPostEvent } from "../../../store/Actions/PostEventAction";
import { FetchGradreelType } from "../../../store/Actions/PreEventAction";
import UploadProgressModal from "../../../components/UploadProgressModal/UploadProgressModal";
import { getLoginUser } from "../../../store/Actions/SharedAction";

const getEventDisplayDate = (eventData) => {
    // Check for combined datetime first
    const combinedEventDateTime = eventData?.eventDateTime
        || eventData?.eventDateTimeInUTC
        || eventData?.EventDateTime
        || eventData?.EventDateTimeInUTC
        || null;

    if (combinedEventDateTime) {
        const parsedDate = new Date(combinedEventDateTime);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    // Check for separate date and time fields
    const rawEventDate = eventData?.eventDate || eventData?.EventDate || '';
    const rawEventTime = eventData?.eventTime || eventData?.EventTime || '';
    
    if (rawEventDate) {
        // If we have date, try to combine with time if available
        const dateTimeString = rawEventTime ? `${rawEventDate}T${rawEventTime}` : rawEventDate;
        const parsedDate = new Date(dateTimeString);
        return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    }

    return null;
};

const PostEventOverview = ({ loading, initializeValues, eventMaster, postEventDetails, postEventData, getCeremonyVideoUri, ceremonyVideoUri, submitPostEvent, dispatchPostRequest, eventSuccess, fetchPostEventData, getEventMaster, uploadedProgress, uploadInProgress, setEventSuccess, handshakeDetails, getHandshakeDetails, postFirstHandshakeTimestampDetailsWithInput, loginUserDetails, getLoginUser, setPostEventDetails }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [dateTime, setDateTime] = useState(() => getEventDisplayDate(postEventDetails));
    const [eventId, setEventId] = useState(postEventDetails?.eventId || '');
    const [eventName, setEventName] = useState(postEventDetails?.eventName || '');
    const [selectedFile, setSelectedFile] = useState(null);
    const [isButtonSelected, setIsButtonSelected] = useState(false);
    const [streamingURL, setStreamingURL] = useState();
    const [ceremonyVideoUrl, setCeremonyVideoUrl] = useState(ceremonyVideoUri?.result)
    const [uploadProgress, setUploadProgress] = useState(uploadedProgress || 0);
    const [eventState, setEventState] = useState('');

    const [isStreamingEvent, setIsStreamingEvent] = useState();
    const [selectedRadioButton, setSelectedRadioButton] = useState(isStreamingEvent ? "variant-2" : "default");

    const [firstHandshakeTimestamp, setFirstHandshakeTimestamp] = useState("");
    const [beginningHandshakeTimestamp, setBeginningHandshakeTimestamp] = useState(null);
    const [endHandshakeTimestamp, setEndHandshakeTimestamp] = useState(null);
    const [handshakeSavedAtLeastOnce, setHandshakeSavedAtLeastOnce] = useState(false);
    const [deepLinkBootstrap, setDeepLinkBootstrap] = useState(false);

    const date = dateTime ? dateTime.toLocaleDateString() : '';
    const time = dateTime ? dateTime.toLocaleTimeString() : '';

    useEffect(() => {
        if (postEventDetails) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const queryValue = params.get('Query') || params.get('query');
        if (queryValue) {
            setDeepLinkBootstrap(true);
            getLoginUser(queryValue);
        } else {
            navigate('/PostEventDetailsDashboard');
        }
    }, [location.search, postEventDetails]);

    useEffect(() => {
        if (!deepLinkBootstrap || postEventDetails) {
            return;
        }

        const result = loginUserDetails?.result;
        if (result?.eventID) {
            // Debug logging to see what fields are available
            console.log('API Result fields:', Object.keys(result));
            console.log('Date/Time related fields:', {
                eventDateTime: result.eventDateTime,
                eventDateTimeInUTC: result.eventDateTimeInUTC,
                EventDateTime: result.EventDateTime,
                EventDateTimeInUTC: result.EventDateTimeInUTC,
                eventDate: result.eventDate,
                EventDate: result.EventDate,
                eventTime: result.eventTime,
                EventTime: result.EventTime
            });
            
            setClientData(result);
            setPostEventDetails({
                clientId: result.clientID,
                eventId: result.eventID,
                eventName: result.eventName || '',
                eventDateTime: result.eventDateTime || result.eventDateTimeInUTC || result.EventDateTime || result.EventDateTimeInUTC || null,
                eventDate: result.eventDate || result.EventDate || '',
                eventTime: result.eventTime || result.EventTime || '',
            });
            setDeepLinkBootstrap(false);
        } else if (loginUserDetails?.isSuccess === false) {
            setDeepLinkBootstrap(false);
            navigate('/PostEventDetailsDashboard');
        }
    }, [deepLinkBootstrap, loginUserDetails?.isSuccess, loginUserDetails?.result?.eventID, postEventDetails]);

    useEffect(() => {
        // Update dateTime when postEventDetails changes
        const newDateTime = getEventDisplayDate(postEventDetails);
        setDateTime(newDateTime);
    }, [postEventDetails]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!postEventDetails) {
                    return;
                }
                await Promise.all([
                    fetchPostEventData(postEventDetails.clientId, postEventDetails.eventId),
                    getEventMaster({ clientId: postEventDetails.clientId, eventId: postEventDetails.eventId }),
                    getCeremonyVideoUri(postEventDetails?.eventId),
                    getHandshakeDetails(postEventDetails?.eventId, postEventDetails?.clientId),
                ]);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        initializeValues();
        fetchData();
    }, [postEventDetails]);

    useEffect(() => {
        const eventData = eventMaster?.result?.eventData;
        const hydratedEventName = eventData?.eventName;
        const hydratedEventDateTime = eventData?.eventDateTime
            || eventData?.eventDateTimeInUTC
            || eventData?.EventDateTime
            || eventData?.EventDateTimeInUTC
            || null;

        const nextEventId = postEventDetails?.eventId || eventData?.eventId || '';
        const nextEventName = hydratedEventName || postEventDetails?.eventName || '';
        const nextEventDateTime = hydratedEventDateTime || postEventDetails?.eventDateTime || null;

        setEventId(nextEventId);
        setEventName(nextEventName);
        setDateTime(getEventDisplayDate({
            eventDateTime: nextEventDateTime,
            eventDate: eventData?.eventDate || eventData?.EventDate || postEventDetails?.eventDate || '',
            eventTime: eventData?.eventTime || eventData?.EventTime || postEventDetails?.eventTime || '',
        }));

        if (!postEventDetails || (!hydratedEventName && !hydratedEventDateTime)) {
            return;
        }

        if (postEventDetails.eventName === nextEventName && postEventDetails.eventDateTime === nextEventDateTime) {
            return;
        }

        setPostEventDetails({
            ...postEventDetails,
            eventName: nextEventName,
            eventDateTime: nextEventDateTime,
            eventDate: eventData?.eventDate || eventData?.EventDate || postEventDetails?.eventDate || '',
            eventTime: eventData?.eventTime || eventData?.EventTime || postEventDetails?.eventTime || '',
        });
    }, [
        eventMaster?.result?.eventData?.eventId,
        eventMaster?.result?.eventData?.eventName,
        eventMaster?.result?.eventData?.eventDateTime,
        eventMaster?.result?.eventData?.eventDateTimeInUTC,
        eventMaster?.result?.eventData?.EventDateTime,
        eventMaster?.result?.eventData?.EventDateTimeInUTC,
        eventMaster?.result?.eventData?.eventDate,
        eventMaster?.result?.eventData?.EventDate,
        eventMaster?.result?.eventData?.eventTime,
        eventMaster?.result?.eventData?.EventTime,
        postEventDetails?.eventId,
        postEventDetails?.eventName,
        postEventDetails?.eventDateTime,
        postEventDetails?.eventDate,
        postEventDetails?.eventTime,
    ]);
    useEffect(() => {
        if (eventSuccess) {
            setEventSuccess(false);
            toastSuccess(`Post event details submitted successfully`);
            // setTimeout(() => {
                const path = isButtonSelected ? "/postEventDetailsDashboard" : "#";
                navigate(path);
            // }, 0);
        }
    }, [eventSuccess]);

    useEffect(() => {
        setStreamingURL(postEventData?.result?.postEventData?.streamingUrl)
        setIsStreamingEvent(postEventData?.result?.postEventData?.isStreamingEvent)
    }, [postEventData?.result?.postEventData])

    useEffect(() => {
        setCeremonyVideoUrl(ceremonyVideoUri?.result)
    }, [ceremonyVideoUri?.result])

    useEffect(() => {
        setEventState(eventMaster?.result?.eventData?.eventState)
        // if (['ProcessingInProgress', 'ProcessingFailed', 'PartialSuccess', 'AssetPublished', 'RequestForProcessing'].includes(eventMaster?.result?.eventData?.eventState)) {
        //     setIsButtonSelected(false);
        // }
        // if (eventMaster?.result?.eventData?.eventStage === 'PostEvent' && eventMaster?.result?.eventData?.eventState !== "RequestForProcessing") {
        //     setIsButtonSelected(false)
        // }
    }, [eventMaster?.result?.eventData?.eventState])

    useEffect(() => {
        if (!uploadInProgress) {
            setIsButtonSelected(true);
        }

    }, [uploadInProgress])

    // Pre-fill handshake fields if API returns existing values
    useEffect(() => {
        const data = handshakeDetails?.result?.handshakeData;
        if (!data) return;

        const ts =
            data.firstHandshakeVideoTimeStamp ??
            data.firstHandshakeVideoTimeStampInhhmmss ??
            data.FirstHandshakeVideoTimeStamp ??
            "";

        const begin =
            data.timeToAddAtBeginningHandshake ??
            data.TimeToAddAtBeginningHandshake ??
            null;

        const end =
            data.timeToAddAtEndHandshake ??
            data.TimeToAddAtEndHandshake ??
            null;

        if (typeof ts === "string") setFirstHandshakeTimestamp(ts);
        if (begin !== null && begin !== undefined && begin !== "") setBeginningHandshakeTimestamp(Number(begin));
        if (end !== null && end !== undefined && end !== "") setEndHandshakeTimestamp(Number(end));
    }, [handshakeDetails]);

    // Sets the ceremony video file selected by user
    const handleFileChange = (file) => {
        setSelectedFile(file);
        setIsButtonSelected(true);
        setIsStreamingEvent(false);
    };

    const handleSelectedHandshakeTimestamp = (timestamp) => {
        setFirstHandshakeTimestamp(timestamp);
    };

    const handleBeginningTimestampChange = (event) => {
        const raw = event.target.value;
        if (raw === "") {
            setBeginningHandshakeTimestamp(null);
            return;
        }
        let newValue = parseInt(raw, 10);
        if (Number.isNaN(newValue)) return;
        newValue = Math.min(Math.max(0, newValue), 60);
        setBeginningHandshakeTimestamp(newValue);
    };

    const handleEndTimestampChange = (event) => {
        const raw = event.target.value;
        if (raw === "") {
            setEndHandshakeTimestamp(null);
            return;
        }
        let newValue = parseInt(raw, 10);
        if (Number.isNaN(newValue)) return;
        newValue = Math.min(Math.max(0, newValue), 60);
        setEndHandshakeTimestamp(newValue);
    };

    // Sets Url added by user
    const streamingURLChange = (url) => {
        setStreamingURL(url)
    }

    // const isHandshakeEdited =
    //     (firstHandshakeTimestamp && firstHandshakeTimestamp.trim().length > 0) ||
    //     beginningHandshakeTimestamp !== null ||
    //     endHandshakeTimestamp !== null;

    const persistHandshakeDetails = async () => {
        const ts = (firstHandshakeTimestamp || "").trim();
        const tsIsValid = /^\d{2}:\d{2}:\d{2}$/.test(ts);
        if (!tsIsValid) {
            toastError("Please enter First Handshake Timestamp in HH:MM:SS format.");
            return false;
        }

        // Default offsets to 0 if user left them blank
        let begin = beginningHandshakeTimestamp;
        let end = endHandshakeTimestamp;

        if (begin === null || begin === undefined || begin === "") begin = 0;
        if (end === null || end === undefined || end === "") end = 0;

        // Enforce 0–60 range as a safety net
        if (begin < 0 || begin > 60 || end < 0 || end > 60) {
            toastError("Beginning and Ending Offset Seconds must be between 0 and 60.");
            return false;
        }

        // Update state so the UI reflects the defaulted values
        setBeginningHandshakeTimestamp(begin);
        setEndHandshakeTimestamp(end);

        const eventIdToUse = postEventData?.result?.postEventData?.eventId ?? postEventDetails?.eventId;
        const clientIdToUse = postEventData?.result?.postEventData?.clientId ?? postEventDetails?.clientId;

        if (!eventIdToUse || !clientIdToUse) {
            toastError("Missing EventId/ClientId; unable to save handshake details.");
            return false;
        }

        const payload = {
            EventId: eventIdToUse,
            ClientId: clientIdToUse,
            FirstHandshakeVideoTimeStamp: ts,
            TimeToAddAtBeginningHandshake: begin,
            TimeToAddAtEndHandshake: end,
            HandshakeClipDurationInSeconds: "",
        };

        const ok = await postFirstHandshakeTimestampDetailsWithInput(payload);
        if (ok) setHandshakeSavedAtLeastOnce(true);
        return !!ok;
    };

    // Uploads the video or url and submit post event
    const handleSavingPostEvent = async (e) => {
        e.preventDefault();
        if (['ProcessingInProgress', 'ProcessingFailed', 'PartialSuccess', 'AssetPublished', 'RequestForProcessing'].includes(eventMaster?.result?.eventData?.eventState)) {
            navigate('/postEventDetailsDashboard');
        } else {

            if (!isButtonSelected) {
                return;
            }
            setUploadProgress(0);

            const clientData = getClientData();
            if (clientData) {
                // Persist handshake fields first so they aren't lost if upload fails/gets cancelled.
                const handshakeSaved = await persistHandshakeDetails();
                if (!handshakeSaved) return;

                if (selectedFile) {
                    // setIsButtonSelected(false);
                    await dispatchPostRequest(ceremonyVideoUrl, selectedFile, postEventData);
                    setIsButtonSelected(true);
                } else if (selectedRadioButton === 'variant-2' || streamingURL) {
                    // setIsButtonSelected(false);
                    const eventData = {
                        eventId: postEventData.result.postEventData.eventId,
                        clientId: postEventData.result.postEventData.clientId,
                        videoFileName: "",
                        isStreamingEvent: true,
                        streamingUrl: streamingURL,
                        submittedBy: clientData.userEmail
                    };
                    await submitPostEvent(eventData);
                    setIsButtonSelected(true);
                } else {
                    toastError('Please select a file or streaming URL.')
                }
            };
        }
    };

    // Redirects to handshake page when user clicks on it
    const handleHandshake = (e) => {
        e.preventDefault();
        window.open(`/HandshakeTimestampComponent?eventId=${postEventDetails?.eventId}&clientId=${postEventDetails?.clientId}`, '_blank');
    }

    return (

        <div className="post-event-overview">
            <div className="div-2">
                <PostEventDetails
                    isStreamingEvent={isStreamingEvent}
                    selectedRadioButton={selectedRadioButton}
                />
                <div className="frame-19">
                    <div className="frame-20">
                        <div className="section-title">
                            <div className="frame-21">
                                <div className="page-title">
                                    <div className="text-wrapper-3">Post Event Details</div>
                                </div>
                                <div className="breadcrum">
                                    <img
                                        className="material-symbols"
                                        alt="Material symbols"
                                        src={homeImage}
                                    />
                                    <img
                                        className="keyboard-arrow-right"
                                        alt="Keyboard arrow right"
                                        src={rightarrow}
                                    />
                                    <div className="text-wrapper-4">Events</div>
                                    <img
                                        className="keyboard-arrow-right"
                                        alt="Keyboard arrow right"
                                        src={rightarrow}
                                    />
                                    <div className="text-wrapper-5">Post Event Details</div>
                                </div>
                            </div>
                        </div>
                        <div className="frame-22">

                            <div className="frame-27">
                                <div className="frame-28">
                                    <div className="frame-29">
                                        <div className="text-wrapper-6">Event details</div>
                                    </div>

                                    <div className="frame-30">
                                        <div className="frame-31">

                                            <Group
                                                className="group-37146"
                                                divClassName="group-37146-instance"
                                                divClassNameOverride="group-2"
                                                frameClassName="group-3"
                                                text={eventName}
                                                text1="Event Name"
                                                visible={false}
                                                visible1={false}
                                            />
                                            <Group
                                                className="group-37146"
                                                divClassName="group-37146-instance"
                                                divClassNameOverride="group-2"
                                                frameClassName="group-3"
                                                text={eventId}
                                                text1="Event ID"
                                                visible={false}
                                                visible1={false}
                                            />
                                        </div>

                                        <div className="frame-31">
                                            <Group
                                                className="group-37146"
                                                divClassName="group-37146-instance"
                                                divClassNameOverride="group-2"
                                                frameClassName="group-3"
                                                text={date}
                                                text1="Event Date"
                                                visible={false}
                                                visible1={false}
                                            />
                                            <Group
                                                className="group-37146"
                                                divClassName="group-37146-instance"
                                                divClassNameOverride="group-2"
                                                frameClassName="group-3"
                                                text={time}
                                                text1="Event Time"
                                                visible={false}
                                                visible1={false}
                                            />
                                        </div>
                                    </div>
                                    <div className="frame-31">

                                        <div className="frame-33">
                                            <div className={"frame-34"}>
                                                <UploadMediaClipSection
                                                    className="design-component-instance-node-2"
                                                    frameWrapperCheckboxPropertyFiRrClassName="audio-clip-section-instance"
                                                    override={<RadioButtons5 className="icon-instance-node" />}
                                                    property1="default"
                                                    radioBtnMusicClipRadioOptionIcon={<RadioButtons4 className="icon-instance-node" />}
                                                    onFileChange={handleFileChange}
                                                    selectedRadioButton={selectedRadioButton}
                                                    onRadioButtonChange={(value) => setSelectedRadioButton(value)}
                                                    isStreamingEvent={isStreamingEvent}
                                                    streamingURLChange={streamingURLChange}
                                                    eventState={eventState}
                                                    submitButtonEnabled={setIsButtonSelected}
                                                />

                                                {/* Add this disclaimer */}
                                                <div className="upload-disclaimer" style={{
                                                    marginTop: '10px',
                                                    padding: '8px 12px',
                                                    backgroundColor: '#fff3cd',
                                                    border: '1px solid #ffeaa7',
                                                    borderRadius: '4px',
                                                    fontSize: '12px',
                                                    color: '#856404',
                                                    lineHeight: '1.4'
                                                }}>
                                                    <strong>Note:</strong> Please use simple filenames without special characters. 
                                                    Long filenames with special characters may cause upload issues.
                                                </div>
                                            </div> <br /><br />
                                            {uploadProgress > 0 &&
                                                <UploadProgressModal />
                                            }
                                            {/* {uploadProgress === 0 && <p style={{ fontFamily: 'Poppins', color: 'blue' }}>Preparing for upload...</p>}

                                            {uploadProgress > 0 && uploadProgress < 100 && (
                                                <Box sx={{ width: '100%' }}>
                                                    <LinearProgressWithLabel value={uploadProgress} />
                                                </Box>
                                            )}

                                            {uploadProgress === 100 && <p style={{ fontFamily: 'Poppins', color: '#3a7848' }}>File uploaded successfully!</p>} */}
                                        </div>
                                    </div>
                                    <div className="frame-28">
                                        <div className="frame-29">
                                            <div className="text-wrapper-6">Handshake Timestamp Details</div>
                                        </div>
                                        <div className="frame-30">
                                            <div className="postevent-handshake-row postevent-handshake-offsets">
                                                <div className="postevent-handshake-field">
                                                    <TimestampFieldComponent
                                                        getTimestamp={handleSelectedHandshakeTimestamp}
                                                        timestamps={firstHandshakeTimestamp}
                                                    />
                                                </div>
                                                <div className="postevent-handshake-field">
                                                    <label
                                                        htmlFor="beginningHandshakeTimestamp"
                                                        className="postevent-handshake-label"
                                                        style={{ fontFamily: "Poppins", width: 'max-content' }}
                                                    >
                                                        Beginning Offset Seconds
                                                    </label>
                                                    <input
                                                        type="number"
                                                        placeholder="0-60"
                                                        id="beginningHandshakeTimestamp"
                                                        value={beginningHandshakeTimestamp ?? ""}
                                                        onChange={handleBeginningTimestampChange}
                                                        className="postevent-handshake-input"
                                                    />
                                                </div>
                                                <div className="postevent-handshake-field">
                                                    <label
                                                        htmlFor="endHandshakeTimestamp"
                                                        className="postevent-handshake-label"
                                                        style={{ fontFamily: "Poppins" }}
                                                    >
                                                        Ending Offset Seconds
                                                    </label>
                                                    <input
                                                        type="number"
                                                        placeholder="0-60"
                                                        id="endHandshakeTimestamp"
                                                        value={endHandshakeTimestamp ?? ""}
                                                        onChange={handleEndTimestampChange}
                                                        className="postevent-handshake-input"
                                                    />
                                                </div>
                                            </div>
                                            {handshakeSavedAtLeastOnce && (
                                                <div className="postevent-handshake-saved">
                                                    Handshake details saved.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>
                    <div className="primary-BTN-wrapper">
                        <div className="primary-BTN-2">
                            <Link to="/PostEventDetailsDashboard" className="button-text-2">Back</Link>
                        </div>
                        <div className={`primary-BTN ${isButtonSelected ? "" : "disabled"}`} onClick={handleSavingPostEvent}>
                            <Link className="button-text">Submit</Link>
                        </div>
                    </div>
                    {loading && (
                        <div className="loader-container">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '10px' }}>
                                <CircularProgress />
                            </Box>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">{`${Math.round(
                    props.value,
                )}%`}</Typography>
            </Box>
        </Box>
    );
}

const mapStateToProps = (state) => {
    return {
        loading: state.postEvent.loading,
        postEventDetails: state.postEvent.postEventDetails,
        postEventData: state.postEvent.postEventData,
        ceremonyVideoUri: state.postEvent.ceremonyVideoUri,
        handshakeDetails: state.postEvent.handshakeDetails,
        eventMaster: state.preEvent.gradreelType,
        eventSuccess: state.postEvent.eventSuccess,
        uploadedProgress: state.postEvent.uploadProgress,
        uploadInProgress: state.postEvent.uploadInProgress,
        loginUserDetails: state.shared.loginUserDetails,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        initializeValues: () => dispatch(initializeValues()),
        getCeremonyVideoUri: (file, eventData) => dispatch(getCeremonyVideoUri(file, eventData)),
        getHandshakeDetails: (eventId, clientId) => dispatch(getHandshakeDetails(eventId, clientId)),
        postFirstHandshakeTimestampDetailsWithInput: (handshakeDetails) => dispatch(postFirstHandshakeTimestampDetailsWithInput(handshakeDetails)),
        submitPostEvent: (eventData) => dispatch(submitPostEvent(eventData)),
        dispatchPostRequest: (url, file, eventData) => dispatch(dispatchPostRequest(url, file, eventData)),
        setEventSuccess: (flag) => dispatch(setEventSuccess(flag)),
        fetchPostEventData: (clientId, eventId) => dispatch(fetchPostEventData(clientId, eventId)),
        getEventMaster: (eventData) => dispatch(FetchGradreelType(eventData)),
        getLoginUser: (query) => dispatch(getLoginUser(query)),
        setPostEventDetails: (details) => dispatch(setPostEventDetails(details)),
    };
};

const ConnectedPostEventOverview = connect(mapStateToProps, mapDispatchToProps)(PostEventOverview);

export { ConnectedPostEventOverview as PostEventOverview };